import { useEffect, useState } from 'react';
import { useAuth } from '../store/AuthContext';
import { getLocationBalance, getProfitReport } from '../api/distributionService';
import { LocationBalance, ProfitReport } from '../models/distribution';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate } from '../utils/formatters';

export default function Dashboard() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<LocationBalance | null>(null);
  const [report, setReport] = useState<ProfitReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date()
  });

  const navigate = useNavigate();

  useEffect(() => {
      console.log('Dashboard component mounted');

    const fetchData = async () => {
      if (!user?.locationId) return;
      
      setLoading(true);
      try {
        const [balanceData, reportData] = await Promise.all([
          getLocationBalance(user.locationId),
          getProfitReport(user.locationId, dateRange.start, dateRange.end)
        ]);
        setBalance(balanceData);
        setReport(reportData);
      } catch (err) {
        console.error('Failed to fetch data', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.locationId, dateRange]);


const handleGenerateReport = async () => {
    if (!user?.locationId) return;
    
    setLoading(true);
    try {
      const data = await getProfitReport(user.locationId, dateRange.start, dateRange.end);
      setReport(data);
    } catch (err) {
      console.error('Failed to generate report', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (type: 'start' | 'end', value: string) => {
    if (!value) return;
    
    const newDate = new Date(value);
    setDateRange(prev => ({
      ...prev,
      [type]: newDate
    }));
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Outlet Dashboard</h1>
        {balance && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-700">{balance.locationName} Balance</h3>
            <div className="flex space-x-4 mt-2">
              <div className="text-center">
                <p className="text-sm text-gray-500">Gas Balance</p>
                <p className="text-xl font-semibold">{balance.gasBalanceKg.toFixed(2)} kg</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Cash Balance</p>
                <p className="text-xl font-semibold">{formatCurrency(balance.cashBalance)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
        
      <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
        <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-800">Profit Report</h2>
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex-1 sm:flex-none">
                <label className="block text-sm text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={dateRange.start.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  className="p-2 border rounded text-sm w-full"
                  max={dateRange.end.toISOString().split('T')[0]}
                />
              </div>
              <div className="flex-1 sm:flex-none">
                <label className="block text-sm text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={dateRange.end.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  className="p-2 border rounded text-sm w-full"
                  min={dateRange.start.toISOString().split('T')[0]}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <button
              onClick={handleGenerateReport}
              disabled={loading || !user?.locationId}
              className={`px-4 py-2 rounded text-white text-sm font-medium ${
                loading || !user?.locationId
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>

      {report && (
  <div className="p-6 space-y-6">
    {/* Metrics Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-green-50 p-4 rounded-lg border border-green-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-green-600">Gas Sales</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(report.totalGasSales)}</p>
          </div>
          <div className="bg-green-100 p-2 rounded-full">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>
      </div>

      <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-purple-600">POS Transactions</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(report.totalPOS)}</p>
          </div>
          <div className="bg-purple-100 p-2 rounded-full">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    {/* Action Buttons */}
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">Sales Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(`/sales/${report.locationId}`)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Gas Sale
          </button>
          <button
            onClick={() => navigate(`/salesreport/${report.locationId}`)}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Sales Report
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">POS Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(`/pos/${report.locationId}`)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New POS Transaction
          </button>
          <button
            onClick={() => navigate(`/posreport/${report.locationId}`)}
            className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            POS Report
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      </div>

      {report && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Report for {report.locationName} ({formatDate(report.startDate)} to {formatDate(report.endDate)})
          </h3>
          {/* Additional detailed report content can go here */}
        </div>
      )}
    </div>
  );
}